import { type Href } from 'expo-router';
import { CloudOff } from 'lucide-react-native';
import { View } from 'react-native';

import { Chevron } from '@/components/ui/Chevron';
import { Icon } from '@/components/ui/Icon';
import { ListRow } from '@/components/ui/ListRow';
import { type Instance } from '@/lib/data';
import { sealedDateTime, timeAgo } from '@/lib/format';

export interface RunRowProps {
  instance: Instance;
  /** Navigation target — renders a real `<a href>` on web (cmd/middle-click, etc.). */
  href?: Href;
  onPress?: () => void;
  /** Run has local changes not yet uploaded — shows a "not synced" cloud glyph. */
  unsynced?: boolean;
}

/** A run row (active or sealed) in the Home History and Runs lists. */
export function RunRow({ instance, href, onPress, unsynced }: RunRowProps) {
  const sealed = instance.status === 'completed';
  const subtitle = sealed
    ? instance.completed_at
      ? `Sealed ${sealedDateTime(instance.completed_at)}`
      : 'Sealed'
    : `Started ${timeAgo(instance.started_at)}`;

  return (
    <ListRow
      href={href}
      onPress={onPress}
      leading={
        <View className="h-8 w-8 items-center justify-center rounded-full bg-well">
          {sealed ? (
            <View
              className="h-[5px] w-[9px] border-b-2 border-l-2 border-muted"
              style={{ transform: [{ rotate: '-45deg' }, { translateY: -1 }] }}
            />
          ) : (
            <View className="h-2 w-2 rounded-full bg-signal" />
          )}
        </View>
      }
      title={instance.title}
      subtitle={subtitle}
      trailing={
        unsynced ? (
          <View className="flex-row items-center gap-2">
            <Icon icon={CloudOff} size={16} colorVar="--color-caution" />
            <Chevron />
          </View>
        ) : undefined
      }
    />
  );
}
